/**
 * Shared display formatting helpers — extracted from card components.
 *
 * These are pure functions (no React) reusable in both cards and detail panels.
 */

// ── Pipeline stages ─────────────────────────────────────────────────────

/**
 * Infer a `<Badge color={...}>` token from a value string via keyword match.
 * Returns a Clay palette name ('blueberry' | 'tangerine' | 'matcha' | 'nightshade').
 * Used where a badge needs a scannable color without a per-value config.
 */
export function badgeColor(value: string): string {
  const v = value.toLowerCase();
  if (v.includes('negotiation') || v.includes('needs action') || v.includes('proposal')) return 'blueberry';
  if (v.includes('discovery') || v.includes('pending')) return 'tangerine';
  if (v.includes('closed won') || v.includes('resolved') || v.includes('approved')) return 'matcha';
  if (v.includes('awaiting') || v.includes('review')) return 'blueberry';
  return 'nightshade';
}

// ── Names ──────────────────────────────────────────────────────────────

/** Convert "blake.johnson@clay.com" → "Blake Johnson" */
export function emailToDisplayName(email: string | null | undefined): string {
  if (!email) return 'User';
  return email.split('@')[0].split('.').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
}

// ── Avatar ──────────────────────────────────────────────────────────────

/** Derive 1-2 letter initials from a name string. */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Fixed palette for avatar background colors. */
const AVATAR_COLORS = [
  'bg-blueberry-500',
  'bg-ube-500',
  'bg-ube-500',
  'bg-dragonfruit-500',
  'bg-pomegranate-500',
  'bg-tangerine-500',
  'bg-tangerine-500',
  'bg-matcha-500',
  'bg-matcha-600',
  'bg-blueberry-400',
] as const;

/** Stable color from a name string — picks from a fixed palette via hash. */
export function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

/** Convert a snake_case or camelCase key to a readable label. */
export function keyToLabel(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
